import * as React from "react";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import CloseIcon from "@mui/icons-material/Close";
import Heading from "../../../component-lib/Atoms/heading/EventEHRHeading";

import * as Yup from "yup";
import { Form, Formik } from "formik";
import { VALIDATION_CONSTANTS } from "../../../utils/validation-constants/validationConstants";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";
import Button from "../../../component-lib/Molecules/button/Button";
import Input from "../../../component-lib/Atoms/Input/Input";
import SelectDropdown from "../../../component-lib/Atoms/Select/Select";
import { generateUniqueSku } from "../../../utils/generateUniqueNumber";

// ADD_INVENTORY_FORM

const ADD_INVENTORY_DETAILS = {
  ITEM_NAME: "itemName",
  ITEM_ID: "itemId",
  TYPE: "type",
  UNIT_OF_MEASURE: "unitOfMeasure",
  QUANTITY: "quantity",
  STOCK_KEEPING_UNIT: "stockKeepingUnit",
  THRESHOLD_VALUE: "thresholdValue",
  SUPPLIER_NAME: "supplierName",
  PURCHASE_UNIT: "purchaseUnit",
  BATCH_NUMBER: "batchNumber",
};

const CATEGORY_TYPE = [
  { label: "Medicine", value: "Medicine" },
  { label: "Medical Supplies", value: "Medical Supplies" },
  { label: "Linen", value: "Linen" },
  { label: "General Supplies", value: "General Supplies" },
  { label: "Medical Equipment", value: "Medical Equipment" },
];

const MEDICATION_UNITS = [
  {
    label: "Tablet",
    value: "Tablet",
  },
  {
    label: "Capsule",
    value: "Capsule",
  },
  {
    label: "Milliliters (mL)",
    value: "Milliliters (mL)",
  },
  {
    label: "Milligrams (mg)",
    value: "Milligrams (mg)",
  },
  {
    label: "Drops (gtts)",
    value: "Drops (gtts)",
  },
  {
    label: "Ampoule",
    value: "Ampoule",
  },
  {
    label: "Pack",
    value: "Pack",
  },
  {
    label: "Puff",
    value: "Puff",
  },
  {
    label: "International Units (IU)",
    value: "International Units (IU)",
  },
  {
    label: "Nebule",
    value: "Nebule",
  },
  {
    label: "Each",
    value: "Each",
  },
];

interface AddInventoryProps {
  defaultSelectedData?: any;
  handleOnSubmittedData?: (formData: any) => void;
  handleClose: () => void;
}

const AddInventory = ({
  defaultSelectedData,
  handleOnSubmittedData,
  handleClose,
}: AddInventoryProps) => {
  // In EDIT mode only Quantity and Threshold are editable (every identity field is
  // disabled). Validating the disabled fields against the strict regexes would block
  // Save when a legacy value (e.g. an item name with parentheses or a "-" batch number)
  // fails the rule, so the edit schema only validates the two editable fields.
  const isEditMode = Boolean(defaultSelectedData?.itemID);

  const quantityRules = Yup.string()
    .matches(
      VALIDATION_CONSTANTS.ID,
      "Quantity must contain only letters and numbers",
    )
    .matches(
      VALIDATION_CONSTANTS.LIMIT_10,
      "Quantity should not greater than 10 digits.",
    )
    .required("Quantity is required");

  // Quantity is a free inventory transaction and may be reduced below the threshold
  // (partial fulfillment), so the threshold-not-greater-than-quantity test is removed.
  const thresholdRules = Yup.string()
    .matches(
      VALIDATION_CONSTANTS.ID,
      "Threshold value must contain only letters and numbers",
    )
    .matches(
      VALIDATION_CONSTANTS.LIMIT_10,
      "Threshold value should not greater than 10 digits.",
    )
    .required("Threshold value is required");

  const validationSchema = isEditMode
    ? Yup.object().shape({
        [ADD_INVENTORY_DETAILS.QUANTITY]: quantityRules,
        [ADD_INVENTORY_DETAILS.THRESHOLD_VALUE]: thresholdRules,
      })
    : Yup.object().shape({
        [ADD_INVENTORY_DETAILS.ITEM_NAME]: Yup.string()
          .matches(
            VALIDATION_CONSTANTS.ACCEPT_CHAR_AND_NUMBER,
            "Item name must contain only letters,numbers and spaces and must not exceed 40 characters.",
          )
          .required("Item name is required"),

        [ADD_INVENTORY_DETAILS.ITEM_ID]: Yup.string().required(
          "Item id is required",
        ),
        [ADD_INVENTORY_DETAILS.TYPE]: Yup.string().required("Type is required"),
        [ADD_INVENTORY_DETAILS.UNIT_OF_MEASURE]: Yup.string().required(
          "Unit of measure is required",
        ),
        [ADD_INVENTORY_DETAILS.QUANTITY]: quantityRules,
        [ADD_INVENTORY_DETAILS.STOCK_KEEPING_UNIT]: Yup.string()
          // .matches(
          //   VALIDATION_CONSTANTS.STRICT_CHAR_PLUS_NUMB_WITH_LIMIT_10,
          //   "SKU must contain both uppercase letters and numbers, with no spaces, and be between 5 and 10 characters long."
          // )
          .required("SKU is required"),
        [ADD_INVENTORY_DETAILS.THRESHOLD_VALUE]: thresholdRules,
        [ADD_INVENTORY_DETAILS.SUPPLIER_NAME]: Yup.string().required(
          "Supplier name is required",
        ),
        [ADD_INVENTORY_DETAILS.PURCHASE_UNIT]: Yup.string().required(
          "Purchase unit is required",
        ),
        [ADD_INVENTORY_DETAILS.BATCH_NUMBER]: Yup.string().required(
          "Batch number is required",
        ),
      });

  const headerStyle = {
    fontWeight: "500",
    fontSize: "16px",
    paddingBottom: "10px",
  };

  const commonGridStyle = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
    // gap: "24px 16px",
  };

  return (
    <Formik
      initialValues={{
        [ADD_INVENTORY_DETAILS.ITEM_NAME]: `${defaultSelectedData?.itemName ? defaultSelectedData?.itemName : ""}`,
        [ADD_INVENTORY_DETAILS.ITEM_ID]: `${defaultSelectedData?.itemID ? defaultSelectedData?.itemID : ""}`,
        [ADD_INVENTORY_DETAILS.TYPE]: `${defaultSelectedData?.category ? defaultSelectedData?.category : ""}`,
        [ADD_INVENTORY_DETAILS.UNIT_OF_MEASURE]: `${defaultSelectedData?.unitOfMeasure ? defaultSelectedData?.unitOfMeasure : ""}`,
        [ADD_INVENTORY_DETAILS.QUANTITY]: `${defaultSelectedData?.currentStock ? defaultSelectedData?.currentStock : ""}`,
        [ADD_INVENTORY_DETAILS.STOCK_KEEPING_UNIT]: `${defaultSelectedData?.stockKeepingUnit ? defaultSelectedData?.stockKeepingUnit : generateUniqueSku()}`,
        [ADD_INVENTORY_DETAILS.THRESHOLD_VALUE]: `${defaultSelectedData?.reorderPoint ? defaultSelectedData?.reorderPoint : ""}`,
        [ADD_INVENTORY_DETAILS.SUPPLIER_NAME]: `${defaultSelectedData?.supplierName ? defaultSelectedData?.supplierName : ""}`,
        [ADD_INVENTORY_DETAILS.PURCHASE_UNIT]: `${defaultSelectedData?.purchaseUnit ? defaultSelectedData?.purchaseUnit : ""}`,
        [ADD_INVENTORY_DETAILS.BATCH_NUMBER]: `${defaultSelectedData?.batchNumber ? defaultSelectedData?.batchNumber : ""}`,
      }}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={(values, { setSubmitting }) => {
        try {
          handleOnSubmittedData && handleOnSubmittedData(values);
          // Handle form data here
          // If needed, call an API or further process the values here
        } catch (error) {
          console.log(error);
        } finally {
          setSubmitting(false); // Reset submitting state
        }
      }}
    >
      {({
        errors,
        submitForm,
        isSubmitting,
        handleReset,
        isValid,
        values,
        touched,
        setFieldValue,
        handleBlur,
        handleSubmit,
      }) => {
        return (
          <Form
            onSubmit={handleSubmit}
            className="h-full flex flex-col justify-between rounded-lg"
          >
            <div className="w-full flex flex-col gap-6 rounded-lg">
              {/* ADDRESS DETAILS */}
              <div>
                <Heading title="Item Details" customStyle={headerStyle} />
                <div style={commonGridStyle}>
                  <Input
                    maxLength={40}
                    textFieldLabel="Item Name"
                    name={ADD_INVENTORY_DETAILS.ITEM_NAME}
                    value={values[ADD_INVENTORY_DETAILS.ITEM_NAME]}
                    onChangeCb={(e) => {
                      setFieldValue(
                        ADD_INVENTORY_DETAILS.ITEM_NAME,
                        e.target.value,
                      );
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[ADD_INVENTORY_DETAILS.ITEM_NAME] &&
                      Boolean(errors[ADD_INVENTORY_DETAILS.ITEM_NAME])
                    }
                    helperText={
                      touched[ADD_INVENTORY_DETAILS.ITEM_NAME]
                        ? errors[ADD_INVENTORY_DETAILS.ITEM_NAME]
                        : undefined
                    }
                  />

                  <Input
                    maxLength={40}
                    textFieldLabel="Item Code/ID"
                    name={ADD_INVENTORY_DETAILS.ITEM_ID}
                    value={values[ADD_INVENTORY_DETAILS.ITEM_ID]}
                    onChangeCb={(e) => {
                      setFieldValue(
                        ADD_INVENTORY_DETAILS.ITEM_ID,
                        e.target.value,
                      );
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[ADD_INVENTORY_DETAILS.ITEM_ID] &&
                      Boolean(errors[ADD_INVENTORY_DETAILS.ITEM_ID])
                    }
                    helperText={
                      touched[ADD_INVENTORY_DETAILS.ITEM_ID]
                        ? errors[ADD_INVENTORY_DETAILS.ITEM_ID]
                        : undefined
                    }
                  />

                  <SelectDropdown
                    label="Type"
                    value={values[ADD_INVENTORY_DETAILS.TYPE]}
                    options={CATEGORY_TYPE}
                    onChangeCb={(e) =>
                      setFieldValue(ADD_INVENTORY_DETAILS.TYPE, e.target.value)
                    }
                    onBlur={handleBlur}
                    error={
                      touched[ADD_INVENTORY_DETAILS.TYPE] &&
                      Boolean(errors[ADD_INVENTORY_DETAILS.TYPE])
                    }
                    helperText={
                      touched[ADD_INVENTORY_DETAILS.TYPE]
                        ? errors[ADD_INVENTORY_DETAILS.TYPE]
                        : undefined
                    }
                  />

                  <SelectDropdown
                    label="Unit of Measure"
                    value={values[ADD_INVENTORY_DETAILS.UNIT_OF_MEASURE]}
                    options={MEDICATION_UNITS}
                    onChangeCb={(e) =>
                      setFieldValue(
                        ADD_INVENTORY_DETAILS.UNIT_OF_MEASURE,
                        e.target.value,
                      )
                    }
                    onBlur={handleBlur}
                    error={
                      touched[ADD_INVENTORY_DETAILS.UNIT_OF_MEASURE] &&
                      Boolean(errors[ADD_INVENTORY_DETAILS.UNIT_OF_MEASURE])
                    }
                    helperText={
                      touched[ADD_INVENTORY_DETAILS.UNIT_OF_MEASURE]
                        ? errors[ADD_INVENTORY_DETAILS.UNIT_OF_MEASURE]
                        : undefined
                    }
                  />

                  <Input
                    // disabled
                    textFieldLabel="Quantity"
                    name={ADD_INVENTORY_DETAILS.QUANTITY}
                    value={values[ADD_INVENTORY_DETAILS.QUANTITY]}
                    type="number"
                    onChangeCb={(e) => {
                      setFieldValue(
                        ADD_INVENTORY_DETAILS.QUANTITY,
                        e.target.value,
                      );
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[ADD_INVENTORY_DETAILS.QUANTITY] &&
                      Boolean(errors[ADD_INVENTORY_DETAILS.QUANTITY])
                    }
                    helperText={
                      touched[ADD_INVENTORY_DETAILS.QUANTITY]
                        ? errors[ADD_INVENTORY_DETAILS.QUANTITY]
                        : undefined
                    }
                  />

                  <Input
                    disabled
                    textFieldLabel="SKU (Stock Keeping Unit)"
                    name={ADD_INVENTORY_DETAILS.STOCK_KEEPING_UNIT}
                    value={values[ADD_INVENTORY_DETAILS.STOCK_KEEPING_UNIT]}
                    onChangeCb={(e) => {
                      setFieldValue(
                        ADD_INVENTORY_DETAILS.STOCK_KEEPING_UNIT,
                        e.target.value,
                      );
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[ADD_INVENTORY_DETAILS.STOCK_KEEPING_UNIT] &&
                      Boolean(errors[ADD_INVENTORY_DETAILS.STOCK_KEEPING_UNIT])
                    }
                    helperText={
                      touched[ADD_INVENTORY_DETAILS.STOCK_KEEPING_UNIT]
                        ? errors[ADD_INVENTORY_DETAILS.STOCK_KEEPING_UNIT]
                        : undefined
                    }
                  />

                  <Input
                    // disabled
                    textFieldLabel="Threshold Value"
                    name={ADD_INVENTORY_DETAILS.THRESHOLD_VALUE}
                    value={values[ADD_INVENTORY_DETAILS.THRESHOLD_VALUE]}
                    type="number"
                    onChangeCb={(e) => {
                      setFieldValue(
                        ADD_INVENTORY_DETAILS.THRESHOLD_VALUE,
                        e.target.value,
                      );
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[ADD_INVENTORY_DETAILS.THRESHOLD_VALUE] &&
                      Boolean(errors[ADD_INVENTORY_DETAILS.THRESHOLD_VALUE])
                    }
                    helperText={
                      touched[ADD_INVENTORY_DETAILS.THRESHOLD_VALUE]
                        ? errors[ADD_INVENTORY_DETAILS.THRESHOLD_VALUE]
                        : undefined
                    }
                  />
                </div>
              </div>
              <div>
                <Heading
                  title="Logistics Information"
                  customStyle={headerStyle}
                />
                <div style={commonGridStyle}>
                  <Input
                    textFieldLabel="Supplier/Vendor Name"
                    maxLength={40}
                    name={ADD_INVENTORY_DETAILS.SUPPLIER_NAME}
                    value={values[ADD_INVENTORY_DETAILS.SUPPLIER_NAME]}
                    onChangeCb={(e) => {
                      setFieldValue(
                        ADD_INVENTORY_DETAILS.SUPPLIER_NAME,
                        e.target.value,
                      );
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[ADD_INVENTORY_DETAILS.SUPPLIER_NAME] &&
                      Boolean(errors[ADD_INVENTORY_DETAILS.SUPPLIER_NAME])
                    }
                    helperText={
                      touched[ADD_INVENTORY_DETAILS.SUPPLIER_NAME]
                        ? errors[ADD_INVENTORY_DETAILS.SUPPLIER_NAME]
                        : undefined
                    }
                  />

                  <Input
                    textFieldLabel="Purchase Price/Unit"
                    name={ADD_INVENTORY_DETAILS.PURCHASE_UNIT}
                    value={values[ADD_INVENTORY_DETAILS.PURCHASE_UNIT]}
                    maxLength={40}
                    onChangeCb={(e) => {
                      setFieldValue(
                        ADD_INVENTORY_DETAILS.PURCHASE_UNIT,
                        e.target.value,
                      );
                    }}
                    size="small"
                    onBlur={handleBlur}
                    error={
                      touched[ADD_INVENTORY_DETAILS.PURCHASE_UNIT] &&
                      Boolean(errors[ADD_INVENTORY_DETAILS.PURCHASE_UNIT])
                    }
                    helperText={
                      touched[ADD_INVENTORY_DETAILS.PURCHASE_UNIT]
                        ? errors[ADD_INVENTORY_DETAILS.PURCHASE_UNIT]
                        : undefined
                    }
                  />

                  <Input
                    maxLength={40}
                    textFieldLabel="Batch Number"
                    name={ADD_INVENTORY_DETAILS.BATCH_NUMBER}
                    value={values[ADD_INVENTORY_DETAILS.BATCH_NUMBER]}
                    onChangeCb={(e) => {
                      setFieldValue(
                        ADD_INVENTORY_DETAILS.BATCH_NUMBER,
                        e.target.value,
                      );
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[ADD_INVENTORY_DETAILS.BATCH_NUMBER] &&
                      Boolean(errors[ADD_INVENTORY_DETAILS.BATCH_NUMBER])
                    }
                    helperText={
                      touched[ADD_INVENTORY_DETAILS.BATCH_NUMBER]
                        ? errors[ADD_INVENTORY_DETAILS.BATCH_NUMBER]
                        : undefined
                    }
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-10 gap-4">
              <Button
                label={TEXT_CONSTANTS?.CANCEL}
                variant="outlined"
                onClickCb={() => {
                  handleClose();
                }}
                style={{
                  width: "120px",
                  height: "44px",
                  padding: "12px 20px",
                  fontSize: "16px",
                  backgroundColor: "white",
                  border: "none",
                  color: "#393939",
                }}
              />
              <Button
                // disabled={!isValid} // Button disabled if form is invalid or submitting
                type="submit"
                label={TEXT_CONSTANTS?.SAVE}
                variant="contained"
                onClickCb={() => {}}
                style={{
                  width: "200px",
                  height: "44px",
                  padding: "12px 20px",
                  fontSize: "16px",
                }}
              ></Button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

// ADD INVENTORY DIALOG

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .MuiPaper-root": {
    BorderAllRounded: "8px",
    Height: "436px",
    width: "708px", // Set your custom width here
    maxWidth: "none", // Prevents Material-UI from overriding your width
  },
}));

interface AddNewInventoryDialogProps {
  isOpen: boolean;
  dialogHeading: string;
  defaultDataForInventory: any;
  handleClose: () => void;
  handleAddInventoryData: (data: any) => void;
}

export default function AddNewInventoryDialog({
  isOpen = false,
  defaultDataForInventory,
  handleClose,
  handleAddInventoryData,
  dialogHeading = "Your Heading Here...",
}: AddNewInventoryDialogProps) {
  return (
    <React.Fragment>
      <BootstrapDialog
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            handleClose();
          }
        }}
        aria-labelledby="customized-dialog-title"
        open={isOpen}
      >
        <div
          className="flex justify-between items-center rounded-lg"
          style={{ margin: "14px 24px" }}
        >
          <Heading title={dialogHeading} />
          <div onClick={handleClose} className="cursor-pointer">
            <CloseIcon />
          </div>
        </div>

        <DialogContent
          dividers
          style={{ padding: "16px 24px 24px 24px", borderRadius: "8px" }}
        >
          <AddInventory
            handleClose={handleClose}
            defaultSelectedData={defaultDataForInventory}
            handleOnSubmittedData={(formData) => {
              handleAddInventoryData(formData);
            }}
          />
        </DialogContent>
      </BootstrapDialog>
    </React.Fragment>
  );
}
